import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  List, ClipboardCheck, MoreHorizontal, Search,
  ClipboardList, Camera, Upload, Download, Printer, UserPlus,
} from 'lucide-react';
import type { Site } from '@/hooks/useSites';

export type ClientsMode = 'list' | 'bedcount';

interface StatItem {
  label: string;
  value: string | number;
  tone?: 'default' | 'success' | 'warning' | 'muted' | 'primary' | 'destructive';
}

interface ClientsTabHeaderProps {
  facilityName: string;
  assignedSites: Site[];
  selectedSiteId: string | null;
  onSelectSiteId: (id: string) => void;
  isSuperAdmin: boolean;
  mode: ClientsMode;
  onModeChange: (mode: ClientsMode) => void;

  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;

  stats: StatItem[];

  // Primary + overflow actions
  onNewIntake: () => void;
  onManageRosters: () => void;
  onSaveSnapshot: () => void;
  onImportExcel: () => void;
  onExportCSV: () => void;
  onPrint: () => void;
}

const toneClass = (tone?: StatItem['tone']) => {
  switch (tone) {
    case 'success': return 'text-success';
    case 'warning': return 'text-warning';
    case 'primary': return 'text-primary';
    case 'destructive': return 'text-destructive';
    case 'muted': return 'text-muted-foreground';
    default: return 'text-foreground';
  }
};

export const ClientsTabHeader: React.FC<ClientsTabHeaderProps> = ({
  facilityName, assignedSites, selectedSiteId, onSelectSiteId, isSuperAdmin,
  mode, onModeChange,
  search, onSearchChange, statusFilter, onStatusFilterChange,
  stats,
  onNewIntake, onManageRosters, onSaveSnapshot, onImportExcel, onExportCSV, onPrint,
}) => {
  const showChipSwitcher = !isSuperAdmin && assignedSites.length > 1;

  return (
    <div className="space-y-3">
      {/* Title + facility */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">
            Clients <span className="text-muted-foreground font-normal">— {facilityName}</span>
          </h1>
          {showChipSwitcher && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {assignedSites.map(s => (
                <button
                  key={s.id}
                  onClick={() => onSelectSiteId(s.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    s.id === selectedSiteId
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          {isSuperAdmin && assignedSites.length > 1 && (
            <div className="mt-2">
              <Select value={selectedSiteId ?? ''} onValueChange={onSelectSiteId}>
                <SelectTrigger className="w-[220px] h-8 text-xs">
                  <SelectValue placeholder="Select facility" />
                </SelectTrigger>
                <SelectContent>
                  {assignedSites.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Stat strip */}
      {stats.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm rounded-md border bg-card px-4 py-2">
          {stats.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <span className="text-border" aria-hidden>·</span>}
              <span className={toneClass(s.tone)}>
                <span className="font-semibold">{s.value}</span>{' '}
                <span className="text-muted-foreground">{s.label}</span>
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Toolbar: mode toggle + search + status + overflow */}
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(val) => { if (val) onModeChange(val as ClientsMode); }}
          className="bg-muted/50 p-1 rounded-lg"
        >
          <ToggleGroupItem
            value="list"
            className="gap-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm px-3 text-xs"
          >
            <List className="h-3.5 w-3.5" />
            List
          </ToggleGroupItem>
          <ToggleGroupItem
            value="bedcount"
            className="gap-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm px-3 text-xs"
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            Bed Count
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {mode === 'list' && (
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="checked-in">Checked In</SelectItem>
              <SelectItem value="checked-out">Checked Out</SelectItem>
              <SelectItem value="pass">Pass</SelectItem>
              <SelectItem value="unbedded">Unbedded</SelectItem>
              <SelectItem value="not_present">Not Present</SelectItem>
              <SelectItem value="reserved">Reserved</SelectItem>
              <SelectItem value="waitlist">Waitlist</SelectItem>
            </SelectContent>
          </Select>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" onClick={onNewIntake} className="h-8 gap-1.5">
            <UserPlus className="h-3.5 w-3.5" />
            New Intake
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" title="More actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={onManageRosters}>
                <ClipboardList className="h-4 w-4 mr-2" />
                Manage Rosters…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onSaveSnapshot}>
                <Camera className="h-4 w-4 mr-2" />
                Save Snapshot of Today
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onImportExcel}>
                <Upload className="h-4 w-4 mr-2" />
                Import from Excel…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onPrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print Roster
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
